import React, { useState, useContext, createContext, ReactNode } from 'react';
import { authService, User } from '../services/authService';

interface AuthContextType {
  user: User | null;
  access: string | null;
  refresh: string | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  refreshTokens: () => Promise<{ success: boolean }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }): React.ReactElement => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [access, setAccess] = useState<string | null>(localStorage.getItem('access'));
  const [refresh, setRefresh] = useState<string | null>(localStorage.getItem('refresh'));

  const login = async (email: string, password: string) => {
    const { access, refresh, user } = await authService.login(email, password);
    setUser(user);
    setAccess(access);
    setRefresh(refresh);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('access', access);
    localStorage.setItem('refresh', refresh);
    return user;
  };

  const logout = () => {
    setUser(null);
    setAccess(null);
    setRefresh(null);
    localStorage.removeItem('user');
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
  };

  // Exchange the stored refresh token for a fresh access token. Used by the
  // clinic-agent WebSocket to recover from "token expired while away".
  const refreshTokens = async (): Promise<{ success: boolean }> => {
    const storedRefresh = refresh ?? localStorage.getItem('refresh');
    if (!storedRefresh) return { success: false };
    try {
      const { access: newAccess } = await authService.refresh(storedRefresh);
      setAccess(newAccess);
      localStorage.setItem('access', newAccess);
      return { success: true };
    } catch (err) {
      console.warn('useAuth: token refresh failed', err);
      return { success: false };
    }
  };

  return (
    <AuthContext.Provider value={{ user, access, refresh, login, logout, refreshTokens }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 